import { Injectable } from '@angular/core';
import { Organization, OrganizationDetailsResponse, PlanSelectionDetails } from '@core/models/org.model';
import { SubscriptionDetails } from '@core/models/subscription.model';
import { UserProfile, UserProfileWithOTP } from '@core/models/user.model';
import {
  EnterpriseOnboardingProgress,
  EnterpriseOnboardingRouteStep,
  ENTERPRISE_ONBOARDING_FALLBACK_ROUTE,
  ENTERPRISE_ONBOARDING_ROUTE_PROGRESS,
} from '@features/enterprises/constants/enterprise-onboarding.constants';
import {
  buildOrganizationStubFromProfile,
  IncompleteResumeTarget,
} from '@features/enterprises/utils/enterprise-onboarding-resume.utils';

const STORAGE_KEY = 'icxbee.enterprise-onboarding';

export interface EnterpriseOnboardingState {
  progress: EnterpriseOnboardingProgress;
  profileWithOtp?: UserProfileWithOTP | null;
  profileDetails?: UserProfile | null;
  organizationDetails?: Organization | null;
  planDetails?: PlanSelectionDetails | null;
  subscriptionDetails?: SubscriptionDetails | null;
}

const EMPTY_STATE: EnterpriseOnboardingState = {
  progress: EnterpriseOnboardingProgress.None,
  profileWithOtp: null,
  profileDetails: null,
  organizationDetails: null,
  planDetails: null,
  subscriptionDetails: null,
};

@Injectable({ providedIn: 'root' })
export class EnterpriseOnboardingService {
  beginNewCustomerOnboarding(): void {
    this.write(EMPTY_STATE);
  }

  resumeIncompleteEnterprise(
    profile: UserProfile,
    target: IncompleteResumeTarget,
    orgDetails?: OrganizationDetailsResponse | null,
  ): EnterpriseOnboardingRouteStep {
    const organizationDetails = buildOrganizationStubFromProfile(profile, orgDetails);
    const subscriptionDetails = orgDetails?.subscriptionResponse ?? null;

    this.patch({
      progress: target.progress,
      profileDetails: profile,
      profileWithOtp: null,
      organizationDetails,
      planDetails: null,
      subscriptionDetails,
    });

    return target.routeStep;
  }

  getState(): EnterpriseOnboardingState {
    return this.read();
  }

  getProgress(): EnterpriseOnboardingProgress {
    return this.read().progress;
  }

  canAccess(step: EnterpriseOnboardingRouteStep): boolean {
    return this.getProgress() === ENTERPRISE_ONBOARDING_ROUTE_PROGRESS[step];
  }

  canAccessWithContext(
    step: EnterpriseOnboardingRouteStep,
    context: { changePlan?: boolean; returnToOrderSummary?: boolean } = {},
  ): boolean {
    if (this.canAccess(step)) {
      return true;
    }

    const progress = this.getProgress();

    if (
      context.changePlan &&
      step === 'add-plan' &&
      progress === EnterpriseOnboardingProgress.PlanSelected
    ) {
      return true;
    }

    if (
      context.returnToOrderSummary &&
      step === 'order-summary' &&
      progress === EnterpriseOnboardingProgress.SubscriptionCreated
    ) {
      return true;
    }

    return false;
  }

  getFallbackRouteStep(): EnterpriseOnboardingRouteStep {
    return ENTERPRISE_ONBOARDING_FALLBACK_ROUTE[this.getProgress()];
  }

  patch(partial: Partial<EnterpriseOnboardingState>): void {
    const current = this.read();
    this.write({
      ...current,
      ...partial,
      progress: partial.progress ?? current.progress,
    });
  }

  setAwaitingOtp(profileWithOtp: UserProfileWithOTP): void {
    this.patch({
      progress: EnterpriseOnboardingProgress.AwaitingOtp,
      profileWithOtp,
      profileDetails: profileWithOtp.user,
    });
  }

  setOtpVerified(profile: UserProfile): void {
    this.patch({
      progress: EnterpriseOnboardingProgress.OtpVerified,
      profileDetails: profile,
    });
  }

  setOrganizationCreated(profile: UserProfile, organization: Organization): void {
    this.patch({
      progress: EnterpriseOnboardingProgress.OrganizationCreated,
      profileDetails: profile,
      organizationDetails: organization,
    });
  }

  setPlanSelected(
    profile: UserProfile,
    organization: Organization | null,
    planDetails: PlanSelectionDetails,
  ): void {
    this.patch({
      progress: EnterpriseOnboardingProgress.PlanSelected,
      profileDetails: profile,
      organizationDetails: organization,
      planDetails,
    });
  }

  setSubscriptionCreated(subscriptionDetails: SubscriptionDetails): void {
    this.patch({
      progress: EnterpriseOnboardingProgress.SubscriptionCreated,
      subscriptionDetails,
    });
  }

  getNavigationState(): {
    profileDetails?: UserProfile;
    organizationDetails?: Organization;
    planDetails?: PlanSelectionDetails;
    profileWithOtp?: UserProfileWithOTP;
    subscriptionDetails?: SubscriptionDetails;
  } {
    const state = this.read();
    return {
      profileDetails: state.profileDetails ?? undefined,
      organizationDetails: state.organizationDetails ?? undefined,
      planDetails: state.planDetails ?? undefined,
      profileWithOtp: state.profileWithOtp ?? undefined,
      subscriptionDetails: state.subscriptionDetails ?? undefined,
    };
  }

  buildNavigationState(
    overrides: Partial<{
      profileDetails: UserProfile;
      organizationDetails: Organization;
      planDetails: PlanSelectionDetails;
      profileWithOtp: UserProfileWithOTP;
      subscriptionDetails: SubscriptionDetails;
      isEdit: boolean;
      skipOnboardingGuard: boolean;
      changePlan: boolean;
      returnToOrderSummary: boolean;
      orderSummary: unknown;
    }> = {},
  ): Record<string, unknown> {
    return {
      ...this.getNavigationState(),
      ...overrides,
    };
  }

  clear(): void {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  private read(): EnterpriseOnboardingState {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return { ...EMPTY_STATE };
      }

      const parsed = JSON.parse(raw) as EnterpriseOnboardingState;
      return {
        ...EMPTY_STATE,
        ...parsed,
        progress: parsed.progress ?? EnterpriseOnboardingProgress.None,
      };
    } catch {
      return { ...EMPTY_STATE };
    }
  }

  private write(state: EnterpriseOnboardingState): void {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }
}
